import { PAYMENT_METHODS, TRANSITIONS } from './enums';
import { getEffectivePaymentMethod } from '../transactions/transaction';

const CARD_WELCOME_GUIDE_TRANSITIONS = new Set([
  TRANSITIONS.ACCEPT,
  TRANSITIONS.OPERATOR_ACCEPT,
  TRANSITIONS.COMPLETE_CUSTOM_OFFER_PAYMENT,
]);

const NON_CARD_WELCOME_GUIDE_TRANSITIONS = new Set([
  TRANSITIONS.CONFIRM_PAYMENT,
  TRANSITIONS.OPERATOR_CONFIRM_PAYMENT,
  TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
  TRANSITIONS.OPERATOR_CONFIRM_PAY_LATER_PAYMENT,
  TRANSITIONS.CONFIRM_CUSTOM_OFFER_PAY_LATER_PAYMENT,
]);

const normalizeForCompare = txt => {
  if (!txt || typeof txt !== 'string') return '';

  return txt
    .normalize('NFKC')
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getEntityId = entityOrId => {
  if (!entityOrId) return null;
  if (typeof entityOrId === 'string') return entityOrId;
  return entityOrId?.id?.uuid || entityOrId?.uuid || null;
};

const getRelationshipId = (entity, relationshipName) => {
  return entity?.relationships?.[relationshipName]?.data?.id?.uuid || null;
};

const getProviderId = transaction => {
  return getEntityId(transaction?.provider) || getRelationshipId(transaction, 'provider');
};

const getCurrentUserId = currentUser => {
  return getEntityId(currentUser);
};

export const isWelcomeGuideMessage = (message, welcomeGuide) => {
  return normalizeForCompare(message?.attributes?.content) === normalizeForCompare(welcomeGuide);
};

export const isWelcomeGuideActivationTransition = ({ paymentMethod, transitionName }) => {
  if (!paymentMethod || !transitionName) {
    return false;
  }

  const normalizedPaymentMethod =
    paymentMethod === 'mobile-banking' ? PAYMENT_METHODS.MOBILE_BANKING : paymentMethod;

  if (normalizedPaymentMethod === PAYMENT_METHODS.CARD) {
    return CARD_WELCOME_GUIDE_TRANSITIONS.has(transitionName);
  }

  if (
    normalizedPaymentMethod === PAYMENT_METHODS.PROMPTPAY ||
    normalizedPaymentMethod === PAYMENT_METHODS.MOBILE_BANKING
  ) {
    return NON_CARD_WELCOME_GUIDE_TRANSITIONS.has(transitionName);
  }

  return false;
};

export const shouldPersistWelcomeGuideMessage = ({
  currentUser,
  provider,
  providerId,
  paymentMethod,
  transaction,
  welcomeGuide,
  transitionName,
}) => {
  if (!welcomeGuide || !transitionName) {
    return false;
  }

  const currentUserId = getCurrentUserId(currentUser);
  const resolvedProviderId = getEntityId(provider) || providerId || getProviderId(transaction);

  if (!currentUserId || !resolvedProviderId || currentUserId !== resolvedProviderId) {
    return false;
  }

  return isWelcomeGuideActivationTransition({
    paymentMethod: paymentMethod || getEffectivePaymentMethod(transaction),
    transitionName,
  });
};

const hasActivatedWelcomeGuide = transaction => {
  const paymentMethod = getEffectivePaymentMethod(transaction);
  const transitions = transaction?.attributes?.transitions || [];

  return transitions.some(transition => {
    return isWelcomeGuideActivationTransition({
      paymentMethod,
      transitionName: transition?.transition,
    });
  });
};

const getWelcomeGuideCreatedAt = transaction => {
  const paymentMethod = getEffectivePaymentMethod(transaction);
  const transitions = transaction?.attributes?.transitions || [];
  const matchingTransition = transitions.find(transition => {
    return isWelcomeGuideActivationTransition({
      paymentMethod,
      transitionName: transition?.transition,
    });
  });

  return (
    matchingTransition?.createdAt ||
    transaction?.attributes?.lastTransitionedAt ||
    transaction?.attributes?.createdAt ||
    new Date()
  );
};

const createSyntheticWelcomeGuideMessage = transaction => {
  const welcomeGuide = transaction?.attributes?.protectedData?.welcomeGuide;
  const provider = transaction?.provider;
  const txId = transaction?.id?.uuid;

  if (!welcomeGuide || !provider || !txId || !hasActivatedWelcomeGuide(transaction)) {
    return null;
  }

  return {
    id: { uuid: `synthetic-welcome-guide-${txId}` },
    type: 'message',
    sender: provider,
    attributes: {
      content: welcomeGuide,
      createdAt: getWelcomeGuideCreatedAt(transaction),
    },
  };
};

export const getDisplayMessagesWithWelcomeGuide = (messages = [], transaction) => {
  const welcomeGuide = transaction?.attributes?.protectedData?.welcomeGuide;
  const provider = transaction?.provider;

  if (!welcomeGuide || !provider) {
    return messages;
  }

  let hasPatchedWelcomeGuide = false;
  const normalizedMessages = messages.map(message => {
    const shouldPatchSender =
      !hasPatchedWelcomeGuide &&
      isWelcomeGuideMessage(message, welcomeGuide) &&
      getEntityId(message?.sender) !== getEntityId(provider);

    if (!shouldPatchSender) {
      return message;
    }

    hasPatchedWelcomeGuide = true;
    return {
      ...message,
      sender: provider,
    };
  });

  const hasWelcomeGuideMessage = normalizedMessages.some(message =>
    isWelcomeGuideMessage(message, welcomeGuide)
  );

  if (hasWelcomeGuideMessage) {
    return normalizedMessages;
  }

  const syntheticWelcomeGuideMessage = createSyntheticWelcomeGuideMessage(transaction);
  return syntheticWelcomeGuideMessage
    ? [syntheticWelcomeGuideMessage, ...normalizedMessages]
    : normalizedMessages;
};
